import { DateTime } from 'luxon';
import { Aux } from '../../AuxillaryService';
import { Log } from '../../AuxillaryService/Log';
import { Signage } from '../../Signage';
import { TemplateWidgetMixin } from '../../Template';
import { WidgetType } from '../types';
import { WidgetWithScheduler } from '../WidgetWithScheduler';
import {
    TimeDateState,
    TimeDateTemplateData,
    TimeDateWidgetConfiguration,
    TimeDateWidgetConfigurationDetails,
    ZodTimeDateWidgetConfigurationDetails,
} from './types';

export class TimeDateWidget extends TemplateWidgetMixin(WidgetWithScheduler) {
    readonly type: WidgetType.TIME_DATE = WidgetType.TIME_DATE;
    declare readonly config: TimeDateWidgetConfigurationDetails;

    /**
     * Whether the widget should display seconds
     */
    #displaySeconds = false;

    /**
     * The state of the TimeDateWidget
     */
    timeState: TimeDateState;

    /**
     * Number of seconds between each time the `Scheduler` runs.
     * Default to one minute.
     */
    schedulerRunInterval = 60;

    constructor(widgetConfiguration: TimeDateWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
        this.timeState = {
            lastUpdated: undefined,
            localTimezone: Signage.localTimeZone,
            localTimestamp: Date.now(),
            configuredTimezone: this.config.timezone,
            configuredFormat: this.config.format,
            currentTime: undefined,
        };
    }

    /**
     * Creates a valid TimeDateWidgetConfigurationDetails using ZOD.
     */
    protected validateConfigurationDetails(
        maybeTimeDateWidgetConfigurationDetails: TimeDateWidgetConfigurationDetails
    ): TimeDateWidgetConfigurationDetails {
        return ZodTimeDateWidgetConfigurationDetails.parse(maybeTimeDateWidgetConfigurationDetails);
    }

    /**
     * Generate the data object which is used to build the template
     */
    protected getRenderContext(): TimeDateTemplateData {
        // make sure `this.config.format` is a string
        if (typeof this.config.format !== 'string') {
            this.config.format = '';
        }

        this.timeState.localTimestamp = Date.now();

        /**
         * Determine if seconds are in the format so we can update every
         * second instead of `this.TIME_CHECK`
         */
        const foundSeconds = this.timeState.configuredFormat?.split('').find((val) => {
            return val === 's';
        });

        if (foundSeconds || this.config.clock_type?.toLowerCase() === 'analog') {
            this.#displaySeconds = true;
        }

        // add widgets to `QueryManager`
        Aux.Service('QueryManager').registerQueryableObject(
            'widgets',
            `widgetsid=${this.id}`,
            (select: string) => {
                switch (select) {
                    case 'state':
                        return Promise.resolve(this.timeState);
                    default:
                        return Promise.reject(new Error('not implemented'));
                }
            }
        );

        const timezoneDates = this.getTimezoneDates();
        return {
            d: timezoneDates[0],
            format: this.config.format,
            /**
             * In the DB model it uses `wobj.widget.widgetsid`, which is depreciated
             *   and instead should look at using `Widget.Id`.
             */
            wobj: {
                widget: {
                    widgetsid: this.id,
                },
                config: {
                    clock_type: this.config.clock_type,
                },
            },
            Widget: { Id: this.id },
            timezones: this.getTimezoneArray(),
        };
    }

    /**
     * Do initial sizing and positioning of the analog clock for each timezone then
     * draw each clock
     */
    drawAnalogClockFirstTime(): void {
        const textSize = this.getTextSize();
        const buffer = 4;
        const timezoneDates = this.getTimezoneDates();
        const vertical =
            this.wrapperElement.clientHeight - (textSize + buffer) * timezoneDates.length >
            this.wrapperElement.clientWidth;
        const adjustedZoneHeight =
            this.wrapperElement.clientHeight - (textSize + buffer) * timezoneDates.length;

        if (vertical) {
            this.updateCanvasVertical(adjustedZoneHeight, textSize, buffer, timezoneDates.length);
        } else {
            this.updateCanvasHorizontal(adjustedZoneHeight, textSize, buffer, timezoneDates.length);
        }
    }

    /**
     * Update the canvas styling vertically.
     */
    updateCanvasVertical(
        adjustedZoneHeight: number,
        textSize: number,
        buffer: number,
        timezoneNum: number
    ): void {
        const clockWidth =
            adjustedZoneHeight / timezoneNum < this.wrapperElement.clientWidth
                ? adjustedZoneHeight / timezoneNum
                : this.wrapperElement.clientWidth;

        this.updateCanvasStyling(clockWidth, buffer, textSize, true, adjustedZoneHeight);
    }

    /**
     * Update the canvas styling horizontally
     */
    updateCanvasHorizontal(
        adjustedZoneHeight: number,
        textSize: number,
        buffer: number,
        timezoneNum: number
    ): void {
        const clockWidth =
            this.wrapperElement.clientWidth / timezoneNum < adjustedZoneHeight
                ? this.wrapperElement.clientWidth / timezoneNum
                : adjustedZoneHeight;

        this.updateCanvasStyling(clockWidth, buffer, textSize, false, adjustedZoneHeight);
    }

    /**
     * Update the canvas and inner labels styling.
     */
    updateCanvasStyling(
        clockWidth: number,
        buffer: number,
        textSize: number,
        vertical: boolean,
        adjustedZoneHeight: number
    ): void {
        const timezoneDates = this.getTimezoneDates();
        const timezones = this.getTimezoneArray();
        let ctr = 0;
        timezoneDates.forEach((timezoneDate) => {
            const canvasElement: HTMLCanvasElement | null = this.wrapperElement.querySelector(
                `#analog_clock_canvas_${this.id}_${ctr}`
            );

            if (canvasElement) {
                canvasElement.width = clockWidth - buffer * 2;
                canvasElement.height = clockWidth - buffer * 2;
                canvasElement.style.position = 'absolute';

                let abbrLabel: HTMLElement | null = null;
                let fullLabel: HTMLElement | null = null;
                let canvasLeftValue: number;

                if (vertical) {
                    canvasElement.style.top = `${
                        buffer + ctr * (clockWidth + buffer + textSize)
                    }px`;
                    canvasLeftValue = (this.wrapperElement.clientWidth - canvasElement.width) / 2;
                    canvasElement.style.left = `${canvasLeftValue}px`;

                    abbrLabel = this.updateLabelStyling(
                        `#abbr${this.id}_${ctr}`,
                        ctr,
                        true,
                        clockWidth,
                        buffer,
                        textSize,
                        adjustedZoneHeight,
                        canvasLeftValue,
                        canvasElement
                    );

                    fullLabel = this.updateLabelStyling(
                        `#full${this.id}_${ctr}`,
                        ctr,
                        true,
                        clockWidth,
                        buffer,
                        textSize,
                        adjustedZoneHeight,
                        canvasLeftValue,
                        canvasElement
                    );
                } else {
                    canvasElement.style.top = `${
                        (adjustedZoneHeight - canvasElement.height) / 2
                    }px`;
                    canvasLeftValue = buffer + ctr * (clockWidth + buffer);
                    canvasElement.style.left = `${canvasLeftValue}px`;

                    abbrLabel = this.updateLabelStyling(
                        `#abbr${this.id}_${ctr}`,
                        ctr,
                        true,
                        clockWidth,
                        buffer,
                        textSize,
                        adjustedZoneHeight,
                        canvasLeftValue,
                        canvasElement
                    );

                    fullLabel = this.updateLabelStyling(
                        `#full${this.id}_${ctr}`,
                        ctr,
                        true,
                        clockWidth,
                        buffer,
                        textSize,
                        adjustedZoneHeight,
                        canvasLeftValue,
                        canvasElement
                    );
                }

                this.drawAnalogClock(
                    canvasElement,
                    timezoneDate,
                    timezones[ctr],
                    fullLabel,
                    abbrLabel,
                    canvasLeftValue
                );
            }

            ctr++;
        });
    }

    /**
     * Update the inner canvas label styling.
     */
    updateLabelStyling(
        queryString: string,
        ctr: number,
        vertical: boolean,
        clockWidth: number,
        buffer: number,
        textSize: number,
        adjustedZoneHeight: number,
        canvasLeftValue: number,
        canvasElement: HTMLCanvasElement
    ): HTMLElement | null {
        const foundElement: HTMLElement | null = this.wrapperElement.querySelector(queryString);
        if (!foundElement) return null;

        foundElement.style.position = 'absolute';
        if (vertical) {
            foundElement.style.top = `${ctr * (clockWidth + buffer + textSize) + clockWidth}px`;
            this.centerElement(canvasElement, canvasLeftValue, foundElement);
        } else {
            foundElement.style.top = `${
                (adjustedZoneHeight - canvasElement.height) / 2 + clockWidth
            }px`;
            this.centerElement(canvasElement, canvasLeftValue, foundElement);
        }

        return foundElement;
    }

    /**
     * Get Date for each timezone configured for widget
     */
    getTimezoneDates(): Date[] {
        const timezones = this.getTimezoneArray();
        let currentDateTime = DateTime.local();
        const timezoneDates: Date[] = [];

        timezones.forEach((timezone) => {
            // use widget's timezone (if set)
            if (this.config.timezone && timezone !== 'auto') {
                currentDateTime = currentDateTime.setZone(timezone);
            }
            //use offset to change date if applied
            if (this.config.offset) {
                currentDateTime = currentDateTime.plus({ days: this.config.offset });
            }

            //change date based on day of week
            if (this.config.weekday && this.config.weekday !== 'None') {
                const weekdayRequired = parseInt(this.config.weekday);
                if (this.config.relative_to_week) {
                    currentDateTime = currentDateTime.minus({
                        days: currentDateTime.weekday - weekdayRequired,
                    });
                } else {
                    if (weekdayRequired >= currentDateTime.weekday) {
                        currentDateTime = currentDateTime.plus({
                            days: weekdayRequired - currentDateTime.weekday,
                        });
                    } else {
                        const leftInWeek = 7 - currentDateTime.weekday;
                        currentDateTime = currentDateTime.plus({
                            days: leftInWeek + weekdayRequired,
                        });
                    }
                }
            }
            // Note: This is NOT the current date as the time zone does not match
            //   the config timezone
            // Note 2: Format the date to a string without timezone so that the new
            //   date will be formatted in the proper timezone.
            let currentDate = new Date(currentDateTime.toFormat('yyyy-LL-dd HH:mm:ss'));
            if (currentDate.toString() === 'Invalid Date') {
                currentDate = new Date();
            }

            timezoneDates.push(currentDate);
        });

        return timezoneDates;
    }

    /**
     * Get array of timezones configured for widget
     */
    getTimezoneArray(): string[] {
        let timezoneArray: string[] = [];
        if (this.config.timezone) {
            timezoneArray = this.config.timezone.split(',');
        }
        return timezoneArray;
    }

    /**
     * Draw an analog clock
     * @param canvas - Canvas in which to draw the clock in
     * @param currentDate - Date object representing timezone
     * @param timezone - String representation of timezone
     * @param fullLabel - Div to place current label
     * @param abbrLabel - Div to place abbreviated label
     * @param canvasLeft - Numerical value of 'canvas.left' (Used for placing labels)
     */
    drawAnalogClock(
        canvas: HTMLCanvasElement,
        currentDate: Date,
        timezone: string | null | undefined,
        fullLabel: HTMLElement | null,
        abbrLabel: HTMLElement | null,
        canvasLeft: number | null
    ): void {
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('no canvas context found');
        }

        const radius = (canvas.height / 2) * 0.9;
        ctx.setTransform(1, 0, 0, 1, canvas.height / 2, canvas.height / 2);

        // Draw clock face
        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, 2 * Math.PI);
        ctx.fillStyle = this.getColor('.analog-background-color');
        ctx.fill();

        let ang: number;
        let num: number;
        // Draw numbers
        ctx.font = `${radius * 0.2}px arial`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillStyle = this.getColor('.analog-num-color');
        ctx.strokeStyle = this.getColor('.analog-num-color');
        for (num = 1; num < 13; num++) {
            ang = (num * Math.PI) / 6;
            ctx.rotate(ang);
            ctx.translate(0, -radius * 0.75);
            ctx.rotate(-ang);
            // Manually moving numbers down a bit because 'middle' seems to be
            // slightly off
            ctx.fillText(num.toString(), 0, radius * 0.01);
            ctx.rotate(ang);
            ctx.translate(0, radius * 0.75);
            ctx.rotate(-ang);
        }

        // Draw ticks
        for (num = 0; num < 60; num++) {
            ang = (num * Math.PI) / 30;
            ctx.rotate(ang);
            ctx.translate(0, -radius * 0.95);

            ctx.lineWidth = num % 5 == 0 ? 3 : 1;
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(0, radius * 0.05);
            ctx.stroke();

            ctx.translate(0, radius * 0.95);
            ctx.rotate(-ang);
        }

        // Draw border
        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, 2 * Math.PI);
        ctx.strokeStyle = this.getColor('.analog-border-color');
        ctx.lineWidth = radius * 0.1;
        ctx.stroke();

        let hour = currentDate.getHours();
        let minute = currentDate.getMinutes();
        let second = currentDate.getSeconds();

        // Hour hand
        hour = hour % 12;
        hour =
            (hour * Math.PI) / 6 + (minute * Math.PI) / (6 * 60) + (second * Math.PI) / (360 * 60);
        this.drawAnalogHand(
            ctx,
            hour,
            radius * 0.5,
            radius * 0.05,
            this.getColor('.analog-minute-hour-color')
        );
        // Minute hand
        minute = (minute * Math.PI) / 30 + (second * Math.PI) / (30 * 60);
        this.drawAnalogHand(
            ctx,
            minute,
            radius * 0.8,
            radius * 0.05,
            this.getColor('.analog-minute-hour-color')
        );
        // Second hand
        second = (second * Math.PI) / 30;
        this.drawAnalogHand(
            ctx,
            second,
            radius * 0.9,
            radius * 0.02,
            this.getColor('.analog-second-color')
        );

        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.05, 0, 2 * Math.PI);
        ctx.fillStyle = this.getColor('.analog-second-color');
        ctx.fill();

        ctx.resetTransform();

        // Timezone label
        if (abbrLabel && canvasLeft && timezone) {
            abbrLabel.innerHTML = DateTime.local().setZone(timezone).toFormat('ZZZZ');
            this.centerElement(canvas, canvasLeft, abbrLabel);
        }

        if (fullLabel && canvasLeft && timezone) {
            fullLabel.innerHTML = DateTime.local().setZone(timezone).toFormat('ZZZZZ');
            this.centerElement(canvas, canvasLeft, fullLabel);
        }
    }

    /**
     * Draws a hand of the analog clock
     */
    drawAnalogHand(
        ctx: CanvasRenderingContext2D,
        pos: number,
        length: number,
        width: number,
        color: string
    ): void {
        ctx.beginPath();
        ctx.lineWidth = width;
        ctx.lineCap = 'square';
        ctx.moveTo(0, 0);
        ctx.rotate(pos);
        ctx.lineTo(0, -length);
        ctx.strokeStyle = color;
        ctx.stroke();
        ctx.rotate(-pos);
    }

    /**
     * Get the color of a certain element
     * @param colorElementSelector - Selector for the color element
     * @returns The color value of the element or an empty string.
     */
    getColor(colorElementSelector: string): string {
        const colorElement = this.wrapperElement.querySelector(colorElementSelector);
        let foundColor = '';

        if (colorElement) {
            const colorStyle = window.getComputedStyle(colorElement);
            foundColor = colorStyle.getPropertyValue('color');
        }

        return foundColor;
    }

    /**
     * Update Widget Content
     */
    schedulerAction(): void {
        if (!this.config.timezone) {
            return;
        }

        const timezones = this.getTimezoneArray();
        const timezoneDates = this.getTimezoneDates();

        this.hasContentToPlay = Boolean(this.config.format);

        // Don't re-calculate and reposition canvases/text if it's already been done
        if (this.config.clock_type === 'analog') {
            let ctr = 0;
            timezoneDates.forEach((timezoneDate) => {
                const canvasElement: HTMLCanvasElement | null = this.wrapperElement.querySelector(
                    `#analog_clock_canvas_${this.id}_${ctr}`
                );

                if (canvasElement) {
                    this.drawAnalogClock(
                        canvasElement,
                        timezoneDate,
                        timezones[ctr],
                        null,
                        null,
                        null
                    );
                }
                ctr++;
            });
        } else {
            this.renderTimeDate();
        }
    }

    /**
     * Run the default logic for startScheduler, however if no #displaySeconds
     *   and there is a schedulerHandle then have it run on the start of the minute.
     */
    startScheduler(): void {
        super.startScheduler();

        if (!this.#displaySeconds && this.schedulerHandle) {
            const secondsLeftInMinute = 60 - new Date(Date.now()).getSeconds();
            this.schedulerHandle.Starting(
                DateTime.local()
                    .plus(secondsLeftInMinute * 1000)
                    .toISO()
            );
        }
    }

    /**
     * Render time DOM from template and make it the content of wrapper element
     */
    async renderTimeDate(): Promise<void> {
        const currentDate = DateTime.local();

        try {
            await this.renderContent();

            this.timeState.lastUpdated = Date.now();
            this.timeState.localTimezone = Signage.localTimeZone;
            this.timeState.localTimestamp = this.timeState.lastUpdated;
            this.timeState.currentTime = `${currentDate.toFormat(
                't'
            )} &#8226; ${currentDate.toFormat('LLLL')} ${currentDate.toFormat('d')}`;
        } catch (err) {
            Log.Error(
                `Failed to render widget content (${this.id}:${this.name})`,
                null,
                __fileline,
                err
            );
        }
    }

    /**
     * Get the clock ready for display and set up the scheduler interval.
     */
    async getReadyForDisplay(): Promise<void> {
        await this.renderTimeDate();
        if (this.config.clock_type === 'analog') {
            this.drawAnalogClockFirstTime();
        }

        if (this.#displaySeconds) {
            this.schedulerRunInterval = 1;
        }
    }

    /**
     * Algins targetElement to be centered with the centerElement horizontally
     * @param centerElement - The target element to be used as a
     * reference
     * @param centerLeft - The distance between the left side of the widget
     * and the left edge of the centerElement canvas
     * @param targetElement - The target element to center
     */
    centerElement(
        centerElement: HTMLElement,
        centerLeft: number,
        targetElement: HTMLElement
    ): void {
        const centerElementWidth = centerElement.clientWidth;
        const centerValue = centerLeft + centerElementWidth / 2;

        const targetElementWidth = targetElement.offsetWidth;
        targetElement.style.left = `${centerValue - targetElementWidth / 2}px`;
    }

    /**
     * Gets the text size of the clock label
     * @returns Font size of the label
     */
    getTextSize(): number {
        let textSize = 0;
        const abbr: HTMLElement | null = this.wrapperElement.querySelector(`#abbr${this.id}_0`);
        const full: HTMLElement | null = this.wrapperElement.querySelector(`#full${this.id}_0`);

        if (abbr && abbr.style.display !== 'none' && full && full.style.display !== 'none') {
            const wrapper: HTMLElement | null = this.wrapperElement.querySelector(
                '.wrapper' + this.id.toString()
            );

            if (wrapper) {
                const wrapperStyle = window.getComputedStyle(wrapper);
                textSize = parseInt(wrapperStyle.getPropertyValue('font-size'), 10);
            }
        }

        return textSize;
    }
}
